import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PRIORITES, libelle } from '../constants';
import { initiales, nomComplet, pourcentage, simplifier } from '../utils/format';
import { referentiels } from '../services/api';

// ─── États : chargement, erreur, vide (règle A97 : chaque page les affiche) ──
export const Chargement = ({ texte = 'Chargement…' }) => (
  <div className="chargement" role="status"><span className="roue" aria-hidden="true" />{texte}</div>
);

export const Erreur = ({ children, onReessayer }) => (
  <div className="erreur-bandeau" role="alert">
    {children}
    {onReessayer && <> {' '}<button type="button" className="btn btn-petit" onClick={onReessayer}>Réessayer</button></>}
  </div>
);

export const Vide = ({ children }) => <div className="vide">{children}</div>;

// ─── Modale et panneau latéral (gabarits du Hub) ─────────────────────────────
// Comme dans le CRM, seul le bouton Fermer (ou Échap) ferme : un clic à côté
// ne fait pas perdre une saisie. Échap ne ferme que la fenêtre du dessus
// (une modale ouverte depuis la fiche d'une tâche, pas la fiche avec).
const pileEchap = [];
function useEchap(onFermer) {
  const ref = useRef(onFermer);
  ref.current = onFermer;
  useEffect(() => {
    const jeton = {};
    pileEchap.push(jeton);
    const f = (e) => { if (e.key === 'Escape' && pileEchap[pileEchap.length - 1] === jeton) ref.current(); };
    window.addEventListener('keydown', f);
    return () => { window.removeEventListener('keydown', f); pileEchap.splice(pileEchap.indexOf(jeton), 1); };
  }, []);
}

export function Modale({ titre, onFermer, actions, children, large, className = '' }) {
  useEchap(onFermer);
  return (
    <div className="modal-overlay">
      <div className={`modal-content ${className}`} role="dialog" aria-modal="true" aria-label={titre} style={large ? { maxWidth: 720 } : undefined}>
        <div className="modal-header">
          <h2 title={titre}>{titre}</h2>
          <button type="button" className="btn btn-petit" onClick={onFermer} aria-label="Fermer">✕</button>
        </div>
        <div className="modal-body">{children}</div>
        {actions && <div className="modal-footer">{actions}</div>}
      </div>
    </div>
  );
}

export function Panneau({ titre, onFermer, children, entete }) {
  useEchap(onFermer);
  return (
    <>
      <div className="panneau-overlay" />
      <aside className="panneau" role="dialog" aria-modal="true" aria-label={titre}>
        <div className="panneau-header">
          <div style={{ minWidth: 0, flex: 1 }}>{entete}</div>
          <button type="button" className="btn btn-petit" onClick={onFermer} aria-label="Fermer">✕</button>
        </div>
        <div className="panneau-body">{children}</div>
      </aside>
    </>
  );
}

// ─── Badges et petits éléments ───────────────────────────────────────────────
export function BadgePriorite({ priorite }) {
  const p = PRIORITES.find((x) => x.cle === priorite) || PRIORITES[2];
  return <span className={`badge ${p.classe}`}>{libelle(PRIORITES, priorite)}</span>;
}

export function BadgeEntreprise({ nom, type }) {
  if (!nom) return null;
  return (
    <span className={`badge badge-sans-point badge-entreprise badge-${type || 'neutre'}`} title={type === 'client' ? 'Client' : 'Concession'}>
      {nom}
    </span>
  );
}

export function Avatar({ prenom, nom, titre }) {
  if (!prenom && !nom) return <span className="avatar avatar-vide" title="Personne">–</span>;
  const n = nomComplet({ prenom, nom });
  return <span className="avatar" title={titre || n} aria-label={n}>{initiales(prenom, nom)}</span>;
}

export function Avancement({ fait, total }) {
  const pc = pourcentage(fait, total);
  return (
    <div className="avancement" title={`${fait} tâche(s) terminée(s) sur ${total}`}>
      <div className="avancement-piste"><div className="avancement-barre" style={{ width: `${pc}%` }} /></div>
      <span>{total ? `${fait}/${total}` : 'aucune tâche'}</span>
    </div>
  );
}

// ─── Référentiels partagés (profils actifs, concessions et clients) ──────────
// Chargés une fois par session de page ; `recharger()` après une modification.
let cache = null;
export function useReferentiels() {
  const [ref, setRef] = useState(cache);
  const [erreur, setErreur] = useState(null);
  const charger = () => Promise.all([referentiels.utilisateurs(), referentiels.entreprises()])
    .then(([utilisateurs, entreprises]) => { cache = { utilisateurs, entreprises }; setRef(cache); })
    .catch(() => setErreur('Listes des profils et des entreprises indisponibles.'));
  useEffect(() => { if (!cache) charger(); }, []);
  return { ...(ref || { utilisateurs: [], entreprises: [] }), pret: !!ref, erreur, recharger: () => { cache = null; return charger(); } };
}

// Liste déroulante concessions / clients groupée.
export function ChoixEntreprise({ entreprises, value, onChange, id, vide = 'Aucune' }) {
  const groupes = [['concession', 'Concessions'], ['client', 'Clients']];
  return (
    <select id={id} value={value || ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
      <option value="">{vide}</option>
      {groupes.map(([type, titre]) => {
        const liste = entreprises.filter((e) => e.type === type);
        return liste.length ? (
          <optgroup key={type} label={titre}>
            {liste.map((e) => <option key={e.id} value={e.id}>{e.nom}{e.concession_nom ? ` (${e.concession_nom})` : ''}</option>)}
          </optgroup>
        ) : null;
      })}
    </select>
  );
}

export function ChoixUtilisateur({ utilisateurs, value, onChange, id, vide = 'Personne' }) {
  return (
    <select id={id} value={value || ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
      <option value="">{vide}</option>
      {utilisateurs.map((u) => <option key={u.id} value={u.id}>{nomComplet(u) || `Profil n° ${u.id}`}</option>)}
    </select>
  );
}

// Ferme un menu au clic à l'extérieur ou sur Échap.
export function useFermeture(ouvert, setOuvert) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ouvert) return undefined;
    const clic = (e) => { if (ref.current && !ref.current.contains(e.target)) setOuvert(false); };
    const echap = (e) => { if (e.key === 'Escape') setOuvert(false); };
    document.addEventListener('mousedown', clic);
    document.addEventListener('keydown', echap);
    return () => { document.removeEventListener('mousedown', clic); document.removeEventListener('keydown', echap); };
  }, [ouvert, setOuvert]);
  return ref;
}

// « Voir les projets de… » : choix de plusieurs personnes dans un menu à cases.
const nomPersonne = (u) => nomComplet(u) || `Profil n° ${u.id}`;
export function ChoixPersonnes({ utilisateurs, valeurs, onChange, moiId }) {
  const [ouvert, setOuvert] = useState(false);
  const [q, setQ] = useState('');
  const ref = useFermeture(ouvert, setOuvert);
  const choisis = utilisateurs.filter((u) => valeurs.includes(u.id));
  // L'utilisateur connecté en tête de liste, puis l'ordre alphabétique du serveur.
  const liste = useMemo(() => {
    const filtre = q.trim().toLowerCase();
    const l = filtre ? utilisateurs.filter((u) => nomPersonne(u).toLowerCase().includes(filtre)) : utilisateurs;
    return [...l.filter((u) => u.id === moiId), ...l.filter((u) => u.id !== moiId)];
  }, [utilisateurs, q, moiId]);
  const basculer = (id) => onChange(valeurs.includes(id) ? valeurs.filter((v) => v !== id) : [...valeurs, id]);

  let resume = 'Tous les projets';
  if (choisis.length === 1) resume = choisis[0].id === moiId ? 'Mes projets' : `Projets de ${nomPersonne(choisis[0])}`;
  else if (choisis.length > 1) resume = `Projets de ${choisis.length} personnes`;

  return (
    <div ref={ref} className="choix-personnes">
      <button
        type="button"
        className={`btn choix-personnes-bouton${choisis.length ? ' actif' : ''}`}
        onClick={() => setOuvert((o) => !o)}
        aria-expanded={ouvert}
        aria-haspopup="true"
        title={choisis.length ? choisis.map(nomPersonne).join(', ') : undefined}
      >
        <span className="choix-personnes-resume">{resume}</span>
        <svg className="icone" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {ouvert && (
        <div className="menu-deroulant choix-personnes-menu" role="dialog" aria-label="Voir les projets de">
          <div className="menu-entete">
            <span>Voir les projets de…</span>
            <span className="choix-personnes-raccourcis">
              {!(valeurs.length === 1 && valeurs[0] === moiId) && <button type="button" className="btn btn-petit" onClick={() => onChange([moiId])}>Mes projets</button>}
              {valeurs.length > 0 && <button type="button" className="btn btn-petit" onClick={() => onChange([])}>Tout le monde</button>}
            </span>
          </div>
          <div className="choix-personnes-recherche">
            <input type="search" placeholder="Rechercher une personne…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Rechercher une personne" autoFocus />
          </div>
          <div className="menu-liste choix-personnes-liste">
            {liste.length === 0 && <div className="vide" style={{ padding: 16 }}>Personne ne correspond.</div>}
            {liste.map((u) => (
              <label key={u.id} className="choix-personnes-ligne">
                <input type="checkbox" checked={valeurs.includes(u.id)} onChange={() => basculer(u.id)} />
                <Avatar prenom={u.prenom} nom={u.nom} />
                <span>{nomPersonne(u)}{u.id === moiId && <span className="faible"> (moi)</span>}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Membres d'un projet : des étiquettes, et un champ de recherche qui propose les
// profils au fil de la frappe (accents et casse ignorés, prénom et nom dans
// n'importe quel ordre). ↑ ↓ pour choisir, Entrée pour ajouter, Retour arrière
// pour retirer le dernier, Échap pour fermer la liste sans fermer la fenêtre.
// `connus` : membres déjà enregistrés, y compris ceux qui ne sont plus dans la
// liste des profils actifs (ils restent visibles, donc retirables).
export function ChoixMembres({ id, utilisateurs, connus = [], valeurs, onChange, responsableId, moiId }) {
  const [q, setQ] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const [actif, setActif] = useState(0);
  const [place, setPlace] = useState(null);
  const champ = useRef(null);
  const saisie = useRef(null);
  const menu = useRef(null);

  const profil = (pid) => utilisateurs.find((u) => u.id === pid) || connus.find((u) => u.id === pid) || { id: pid };
  const responsable = responsableId ? profil(responsableId) : null;
  const etiquettes = valeurs.filter((v) => v !== responsableId).map(profil);

  const correspond = (u, mots) => {
    const nom = simplifier(nomPersonne(u));
    return mots.every((m) => nom.includes(m));
  };
  const mots = simplifier(q).split(/\s+/).filter(Boolean);
  const pris = (u) => u.id === responsableId || valeurs.includes(u.id);
  // Comme dans « Voir les projets de… » : soi d'abord, puis l'ordre du serveur.
  const liste = useMemo(() => {
    const l = utilisateurs.filter((u) => !pris(u) && correspond(u, mots));
    return [...l.filter((u) => u.id === moiId), ...l.filter((u) => u.id !== moiId)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilisateurs, valeurs, responsableId, moiId, q]);
  const indice = Math.min(actif, Math.max(liste.length - 1, 0));

  // La fenêtre défile (overflow) : un menu en position absolue y serait coupé.
  // Il est donc placé par rapport à l'écran, au-dessus du champ s'il manque de place.
  useLayoutEffect(() => {
    if (!ouvert) return undefined;
    const placer = () => {
      const r = champ.current.getBoundingClientRect();
      const dessous = window.innerHeight - r.bottom - 12;
      const dessus = r.top - 12;
      const enHaut = dessous < 200 && dessus > dessous;
      setPlace({
        left: r.left,
        width: r.width,
        maxHeight: Math.min(260, enHaut ? dessus - 4 : dessous - 4),
        ...(enHaut ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    };
    placer();
    window.addEventListener('resize', placer);
    window.addEventListener('scroll', placer, true);
    return () => { window.removeEventListener('resize', placer); window.removeEventListener('scroll', placer, true); };
  }, [ouvert, valeurs, responsableId]);

  // La ligne active reste visible quand on descend au clavier.
  useEffect(() => {
    if (ouvert && menu.current) menu.current.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [ouvert, indice]);

  const ajouter = (u) => { onChange([...valeurs, u.id]); setQ(''); setActif(0); };
  const retirer = (pid) => { onChange(valeurs.filter((v) => v !== pid)); saisie.current?.focus(); };

  function clavier(e) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!ouvert) return setOuvert(true);
      if (liste.length) setActif((indice + (e.key === 'ArrowDown' ? 1 : liste.length - 1)) % liste.length);
    } else if (e.key === 'Enter') {
      e.preventDefault(); // Entrée ici ajoute une personne, elle ne valide jamais le formulaire.
      if (ouvert && liste[indice]) ajouter(liste[indice]);
    } else if (e.key === 'Escape' && ouvert) {
      e.stopPropagation(); // Sinon la fenêtre se fermerait avec la liste.
      setOuvert(false);
    } else if (e.key === 'Backspace' && !q && etiquettes.length) {
      retirer(etiquettes[etiquettes.length - 1].id);
    }
    return undefined;
  }

  const dejaMembre = mots.length > 0 && [...etiquettes, ...(responsable ? [responsable] : [])].some((u) => correspond(u, mots));

  return (
    <div className="choix-membres">
      {/* Un clic n'importe où dans le cadre revient à cliquer dans le champ de recherche. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div ref={champ} className="choix-membres-champ" onClick={() => { saisie.current.focus(); setOuvert(true); }}>
        {responsable && (
          <span className="choix-membres-etiquette responsable" title="Le responsable est toujours membre du projet.">
            <Avatar prenom={responsable.prenom} nom={responsable.nom} />
            <span className="choix-membres-nom">{nomPersonne(responsable)}</span>
            <span className="choix-membres-role">Responsable</span>
          </span>
        )}
        {etiquettes.map((u) => (
          <span key={u.id} className="choix-membres-etiquette">
            <Avatar prenom={u.prenom} nom={u.nom} />
            <span className="choix-membres-nom">{nomPersonne(u)}</span>
            <button
              type="button"
              className="choix-membres-retirer"
              aria-label={`Retirer ${nomPersonne(u)}`}
              title="Retirer du projet"
              onClick={(e) => { e.stopPropagation(); retirer(u.id); }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </span>
        ))}
        <input
          ref={saisie}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={ouvert}
          aria-controls={`${id}-liste`}
          aria-autocomplete="list"
          aria-activedescendant={ouvert && liste[indice] ? `${id}-option-${liste[indice].id}` : undefined}
          autoComplete="off"
          placeholder="Ajouter une personne…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setActif(0); setOuvert(true); }}
          onKeyDown={clavier}
          onBlur={() => setOuvert(false)}
        />
      </div>
      {ouvert && place && (
        // Le clic dans la liste ne doit pas retirer le focus du champ (et donc fermer la liste).
        <div
          ref={menu}
          id={`${id}-liste`}
          role="listbox"
          aria-label="Personnes à ajouter"
          className="choix-membres-menu"
          style={place}
          onMouseDown={(e) => e.preventDefault()}
        >
          {liste.length === 0 && (
            <div className="choix-membres-vide">
              {mots.length === 0 && 'Tout le monde est déjà membre.'}
              {mots.length > 0 && (dejaMembre ? 'Cette personne est déjà membre du projet.' : 'Personne ne correspond.')}
            </div>
          )}
          {liste.map((u, i) => (
            // Le clavier passe par le champ de recherche (aria-activedescendant).
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              key={u.id}
              id={`${id}-option-${u.id}`}
              role="option"
              aria-selected={i === indice}
              className={`choix-membres-option${i === indice ? ' active' : ''}`}
              onClick={() => ajouter(u)}
              onMouseMove={() => { if (i !== indice) setActif(i); }}
            >
              <Avatar prenom={u.prenom} nom={u.nom} />
              <span>{nomPersonne(u)}{u.id === moiId && <span className="faible"> (moi)</span>}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Petites icônes en SVG (pas d'émoji : leur rendu dépend des polices du poste).
const svg = { className: 'icone', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
export const IconeCalendrier = () => (
  <svg {...svg}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
);
export const IconeTrombone = () => (
  <svg {...svg}><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
);
export const IconeTelecharger = () => (
  <svg {...svg}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
);
export const IconeFichier = () => (
  <svg {...svg}><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5z" /><path d="M14 2v6h6" /></svg>
);
export const IconeImage = () => (
  <svg {...svg}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.09-3.09a2 2 0 0 0-2.82 0L6 21" /></svg>
);
export const IconeMessage = () => (
  <svg {...svg}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
);
